/**
 * useFeatureAccess — hook for checking feature access against the user's plan.
 *
 * Returns the current access level and a `tryAccess` action for the given
 * feature key. If the feature key has not been registered, access defaults to
 * `'full'` so that unregistered code is never accidentally blocked.
 *
 * Example usage:
 *
 *   const { access, tryAccess } = useFeatureAccess('ai_slicing');
 *
 *   // Hide a UI element entirely:
 *   if (access === 'hidden') return null;
 *
 *   // Gate an action:
 *   const handleClick = () => {
 *     if (!tryAccess()) return;   // shows upgrade toast when locked
 *     doTheThing();
 *   };
 */

import { useStore } from '../store/useStore';
import { getFeatureDefinition, type FeatureAccess } from './featureRegistry';
import { usePricingStore } from './pricingStore';
import { UpgradeMessageKey } from './upgradeMessages';
import { PRICING_TIERS } from './pricingTiers';
import { resolveEffectiveTier, isZeytuneTeam } from './userCategories';

export function useFeatureAccess(key: string): {
    /** The resolved access level for the current user's plan. */
    access: FeatureAccess;

    /**
     * Attempts to use the feature.
     * - Returns `true` when access is `'full'`.
     * - Shows the upgrade prompt and returns `false` when access is `'locked'`.
     * - Returns `false` silently when access is `'hidden'`.
     */
    tryAccess: () => boolean;
} {
    // All hooks must be called unconditionally before any conditional logic.
    const user = useStore((s) => s.user);
    const showUpgradePrompt = usePricingStore((s) => s.showUpgradePrompt);

    // Zeytune team members bypass all tier-based checks and always have full access.
    if (isZeytuneTeam(user)) {
        return { access: 'full', tryAccess: () => true };
    }

    const plan = resolveEffectiveTier(user);
    const feature = getFeatureDefinition(key);

    // Unregistered features are treated as fully accessible to avoid accidental lockouts.
    const access: FeatureAccess = feature?.access[plan] ?? 'full';

    const tryAccess = (): boolean => {
        if (access === 'full') return true;

        if (access === 'locked' && feature) {
            // Compute the minimum required tier name like the previous helper did
            const orderedTiers = Object.values(PRICING_TIERS).sort((a, b) => a.rank - b.rank);
            const firstFullTier = orderedTiers.find((tier) => feature.access[tier.id] === 'full');
            const requiredTierName = firstFullTier?.name ?? 'a higher plan';

            showUpgradePrompt(UpgradeMessageKey.FeatureLocked, {
                feature: feature.description,
                requiredTier: requiredTierName,
            });
        }

        return false;
    };

    return { access, tryAccess };
}
